import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useGql } from '@ah/design';
import type { Conversation } from '../shared/types.ts';
import { CONVERSATIONS_QUERY, CONV_UPDATED_SUB } from '../shared/queries.ts';

export function MessagesBell() {
  const gql = useGql();
  const navigate = useNavigate();
  const [convs, setConvs] = useState<Map<string, Conversation>>(new Map());

  useEffect(() => {
    let mounted = true;
    gql
      .request<{ conversations: Conversation[] }>(CONVERSATIONS_QUERY)
      .then((d) => {
        if (!mounted) return;
        const m = new Map<string, Conversation>();
        for (const c of d.conversations) m.set(c.id, c);
        setConvs(m);
      })
      .catch(() => {});

    const unsub = gql.subscribe<{ onConversationUpdated: Conversation }>(
      CONV_UPDATED_SUB,
      {},
      (data) => {
        setConvs((prev) => {
          const next = new Map(prev);
          next.set(data.onConversationUpdated.id, data.onConversationUpdated);
          return next;
        });
      },
    );

    const onRead = (ev: Event) => {
      const detail = (ev as CustomEvent<{ conversationId?: string }>).detail;
      if (!detail?.conversationId) return;
      setConvs((prev) => {
        const existing = prev.get(detail.conversationId!);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(existing.id, { ...existing, unreadCount: 0 });
        return next;
      });
    };
    window.addEventListener('chat:read', onRead as EventListener);

    return () => {
      mounted = false;
      unsub();
      window.removeEventListener('chat:read', onRead as EventListener);
    };
  }, [gql]);

  const totalUnread = Array.from(convs.values()).reduce(
    (sum, c) => sum + (c.unreadCount ?? 0),
    0,
  );

  return (
    <button
      type="button"
      onClick={() => navigate('/chat')}
      aria-label={totalUnread > 0 ? `Messages (${totalUnread} unread)` : 'Messages'}
      className="relative flex items-center justify-center w-9 h-9 rounded-lg text-foreground hover:bg-accent"
    >
      <MessageCircle size={20} />
      {totalUnread > 0 && (
        <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none">
          {totalUnread > 9 ? '9+' : totalUnread}
        </span>
      )}
    </button>
  );
}
